import LogRocket from 'logrocket';

// Track if LogRocket has been initialized
let isInitialized = false;
let config: { LOGROCKET_ENABLED?: boolean; LOGROCKET_ENVIRONMENT?: string } | null = null;

// Load configuration
const loadConfig = async (): Promise<{ LOGROCKET_ENABLED?: boolean; LOGROCKET_ENVIRONMENT?: string }> => {
  if (config) return config;
  
  try {
    const response = await fetch('/config.json');
    const loadedConfig = await response.json();
    config = loadedConfig;
    return loadedConfig;
  } catch (error) {
    console.warn('Failed to load LogRocket config, defaulting to disabled:', error);
    const defaultConfig = { LOGROCKET_ENABLED: false, LOGROCKET_ENVIRONMENT: 'unknown' };
    config = defaultConfig;
    return defaultConfig;
  }
};

// Utility function to mark sensitive elements for LogRocket exclusion
export const markSensitiveElement = (element: HTMLElement, reason: string = 'payment-form') => {
  element.setAttribute('data-lr-exclude', 'true');
  element.setAttribute('data-lr-sensitive', 'true');
  element.setAttribute('data-lr-reason', reason);
  element.setAttribute('data-lr-ignored', 'true');
};

// Utility function to mark payment forms as sensitive
export const markPaymentForm = (form: HTMLFormElement) => {
  markSensitiveElement(form, 'payment-form');
  
  // Mark all input elements within the form as sensitive
  const inputs = form.querySelectorAll('input, select, textarea');
  inputs.forEach(input => {
    if (input instanceof HTMLElement) {
      markSensitiveElement(input, 'payment-input');
    }
  });
  
  // Mark any Stripe elements as sensitive
  const stripeElements = form.querySelectorAll('[data-stripe]');
  stripeElements.forEach(element => {
    if (element instanceof HTMLElement) {
      markSensitiveElement(element, 'stripe-element');
    }
  });
};

// Check if LogRocket is enabled
export const isLogRocketEnabled = async (): Promise<boolean> => {
  const config = await loadConfig();
  return config.LOGROCKET_ENABLED === true;
};

// LogRocket configuration and utilities
export const configureLogRocket = async () => {
  if (isInitialized) return;
  
  const enabled = await isLogRocketEnabled();
  if (!enabled) {
    console.log('LogRocket is disabled via feature flag');
    return;
  }
  
  const config = await loadConfig();
  
  // Initialize LogRocket with your app ID
  LogRocket.init('3dek6c/frozendeed-v2');
  
  // Enhanced security configuration for payment forms
  // This ensures sensitive payment data is never recorded
  const originalQuerySelector = document.querySelector;
  document.querySelector = function(selector: string) {
    const element = originalQuerySelector.call(this, selector);
    if (element && element.hasAttribute('data-lr-exclude')) {
      // Mark excluded elements to prevent LogRocket recording
      element.setAttribute('data-lr-ignored', 'true');
    }
    return element;
  };
  
  isInitialized = true;
  console.log(`LogRocket initialized for environment: ${config.LOGROCKET_ENVIRONMENT || 'unknown'} with enhanced security for payment forms`);
};

// React Router integration for LogRocket
export const setupLogRocketRouter = async (history: { listen: (callback: (location: Location) => void) => void }) => {
  const enabled = await isLogRocketEnabled();
  if (!enabled) return;
  
  // Ensure LogRocket is initialized before setting up router tracking
  await configureLogRocket();
  
  // Track route changes
  history.listen((location: Location) => {
    LogRocket.track('Navigation', {
      pathname: location.pathname,
      search: location.search,
      hash: location.hash,
    });
  });
};

// Utility function to start a new session
export const startNewSession = async () => {
  const enabled = await isLogRocketEnabled();
  if (!enabled) return;
  
  // Ensure LogRocket is initialized before starting new session
  await configureLogRocket();
  LogRocket.startNewSession();
};

// Utility function to identify users
export const identifyUser = async (username: string, userInfo: {
  name?: string;
  email?: string;
  realm?: string;
  clientId?: string;
  [key: string]: string | number | boolean | undefined;
}) => {
  const enabled = await isLogRocketEnabled();
  if (!enabled) return;
  
  // Ensure LogRocket is initialized before identifying user
  await configureLogRocket();
  
  // Start a new session before identifying the user
  await startNewSession();
  
  // Filter out undefined values before passing to LogRocket
  const filteredUserInfo: Record<string, string | number | boolean> = {};
  Object.entries(userInfo).forEach(([key, value]) => {
    if (value !== undefined) {
      filteredUserInfo[key] = value;
    }
  });
  
  // Add environment information to user identification
  const config = await loadConfig();
  if (config.LOGROCKET_ENVIRONMENT) {
    filteredUserInfo.environment = config.LOGROCKET_ENVIRONMENT;
  }
  
  LogRocket.identify(username, filteredUserInfo);
};

// Utility function to track custom events
export const trackEvent = async (eventName: string, properties?: Record<string, string | number | boolean | string[] | number[] | boolean[] | null | undefined>) => {
  const enabled = await isLogRocketEnabled();
  if (!enabled) return;
  
  // Ensure LogRocket is initialized before tracking events
  await configureLogRocket();
  LogRocket.track(eventName, properties);
};

// Utility function to capture exceptions
export const captureException = async (error: Error, extra?: Record<string, unknown>) => {
  const enabled = await isLogRocketEnabled();
  if (!enabled) return;
  
  // Ensure LogRocket is initialized before capturing exceptions
  await configureLogRocket();
  LogRocket.captureException(error, extra);
};

// Utility function to capture messages
export const captureMessage = async (message: string) => {
  const enabled = await isLogRocketEnabled();
  if (!enabled) return;
  
  // Ensure LogRocket is initialized before capturing messages
  await configureLogRocket();
  LogRocket.captureMessage(message);
};

export default LogRocket;
